import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Plus, Pencil, Trash2, Loader2, Clock, Users, Tag, Sparkles } from 'lucide-react';
import { customChargeTemplatesAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const MODE_META = {
  hourly:     { label: 'Hourly',     Icon: Clock,  unit: '/hr' },
  per_person: { label: 'Per Person', Icon: Users,  unit: '/pp' },
  flat:       { label: 'Flat',       Icon: Tag,    unit: 'flat' },
};

const EMPTY_FORM = { name: '', pricing_mode: 'flat', rate: '', default_hours: '', sort_order: 0 };

const CustomChargeTemplatesManagement = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const { toast } = useToast();

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await customChargeTemplatesAPI.getAll();
      setTemplates(data);
    } catch (e) {
      toast({ title: 'Error loading templates', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const openDialog = (tpl = null) => {
    if (tpl) {
      setEditing(tpl);
      setForm({
        name: tpl.name,
        pricing_mode: tpl.pricing_mode,
        rate: String(tpl.rate),
        default_hours: tpl.default_hours != null ? String(tpl.default_hours) : '',
        sort_order: tpl.sort_order || 0,
      });
    } else {
      setEditing(null);
      setForm(EMPTY_FORM);
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.rate) {
      toast({ title: 'Name and Rate are required', variant: 'destructive' });
      return;
    }
    try {
      setSaving(true);
      const payload = {
        name: form.name,
        pricing_mode: form.pricing_mode,
        rate: parseFloat(form.rate),
        default_hours: form.pricing_mode === 'hourly' && form.default_hours ? parseFloat(form.default_hours) : null,
        sort_order: parseInt(form.sort_order) || 0,
      };
      if (editing) {
        await customChargeTemplatesAPI.update(editing.id, payload);
        toast({ title: 'Template updated' });
      } else {
        await customChargeTemplatesAPI.create(payload);
        toast({ title: 'Template created' });
      }
      setDialogOpen(false);
      fetchTemplates();
    } catch (e) {
      toast({ title: 'Error saving template', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Delete "${tpl.name}"?`)) return;
    try {
      await customChargeTemplatesAPI.delete(tpl.id);
      toast({ title: 'Template deleted' });
      fetchTemplates();
    } catch (e) {
      toast({ title: 'Error deleting template', variant: 'destructive' });
    }
  };

  const handleSeed = async () => {
    try {
      setSeeding(true);
      const res = await customChargeTemplatesAPI.seedDefaults();
      toast({
        title: `Seeded ${res.created.length} default templates`,
        description: res.skipped > 0 ? `${res.skipped} already existed` : undefined,
      });
      fetchTemplates();
    } catch (e) {
      toast({ title: 'Error seeding defaults', variant: 'destructive' });
    } finally {
      setSeeding(false);
    }
  };

  const formatRate = (tpl) => {
    const { unit } = MODE_META[tpl.pricing_mode];
    return `$${tpl.rate.toFixed(2)}${unit === 'flat' ? ' flat' : unit}`;
  };

  return (
    <div data-testid="custom-charge-templates-management">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
        <div>
          <p className="text-gray-600">Quick-add presets for the "Staff & Custom Charges" section on contracts.</p>
          <p className="text-xs text-gray-500 mt-1">
            Staff will see these as one-click buttons on every new contract — saves time and keeps pricing consistent.
          </p>
        </div>
        <div className="flex gap-2">
          {templates.length === 0 && (
            <Button onClick={handleSeed} disabled={seeding} variant="outline" className="border-[#c9a227] text-[#c9a227]" data-testid="seed-templates-btn">
              {seeding ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Sparkles size={16} className="mr-2" />}
              Seed Defaults
            </Button>
          )}
          <Button onClick={() => openDialog()} className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white" data-testid="add-template-btn">
            <Plus size={18} className="mr-2" />Add Template
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#1e3a5f]" size={32} /></div>
      ) : templates.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border">
          <Sparkles size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 mb-3">No templates yet. Click "Seed Defaults" for 8 common presets, or add your own.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(tpl => {
            const { label, Icon } = MODE_META[tpl.pricing_mode];
            return (
              <Card key={tpl.id} className="border-none shadow-sm" data-testid={`template-card-${tpl.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-[#1e3a5f] truncate">{tpl.name}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                          <Icon size={12} /> {label}
                        </span>
                        <span className="text-sm font-bold text-[#c9a227]">{formatRate(tpl)}</span>
                      </div>
                      {tpl.pricing_mode === 'hourly' && tpl.default_hours && (
                        <p className="text-xs text-gray-500 mt-1">Default: {tpl.default_hours} hrs</p>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-end gap-1 pt-2 border-t mt-2">
                    <Button size="sm" variant="ghost" onClick={() => openDialog(tpl)} className="text-[#1e3a5f] h-8" data-testid={`edit-template-${tpl.id}`}>
                      <Pencil size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(tpl)} className="text-red-600 h-8" data-testid={`delete-template-${tpl.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a5f]">{editing ? 'Edit Template' : 'Add Custom Charge Template'}</DialogTitle>
            <DialogDescription>These appear as quick-add buttons on the Contracts page.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Name *</Label>
              <Input data-testid="template-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Corkage Fee" />
            </div>
            <div>
              <Label>Pricing Mode *</Label>
              <Select value={form.pricing_mode} onValueChange={(v) => setForm({ ...form, pricing_mode: v })}>
                <SelectTrigger data-testid="template-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="per_person">Per Person</SelectItem>
                  <SelectItem value="flat">Flat</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Rate ($) *</Label>
                <Input data-testid="template-rate-input" type="number" step="0.01" min="0" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} />
              </div>
              {form.pricing_mode === 'hourly' && (
                <div>
                  <Label>Default Hours</Label>
                  <Input data-testid="template-default-hours-input" type="number" step="0.5" min="0" value={form.default_hours} onChange={(e) => setForm({ ...form, default_hours: e.target.value })} placeholder="e.g., 6" />
                </div>
              )}
              <div className={form.pricing_mode === 'hourly' ? 'col-span-2' : ''}>
                <Label>Sort Order</Label>
                <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white" data-testid="save-template-btn">
              {saving && <Loader2 size={16} className="mr-2 animate-spin" />}
              {editing ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomChargeTemplatesManagement;
